import React, { useState } from 'react';
import { Grid2, Typography, Box, Button, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SearchLicensePlate from './SearchLicensePlate';
import SampleReportPdfModal from './SampleReportPdfModal';
import background_auto_valid from '../../assets/images/background_plate_search.mp4';
import background_auto_valid_mobile from '../../assets/images/background_plate_search_mobile.mp4';
import reportPreviewSymbol from '../../assets/images/report_preview_symbol.png';
import { brandTokens } from '../single-page/theme';

export { SAMPLE_REPORT_PDF_PATH } from './SampleReportPdfModal';

/**
 * Official trust badges for the hero. Empty array = section omitted (no fake seals).
 * Example item: { id: 'ssl', label: 'SSL Secure', icon: 'lock' }
 */
export const HERO_TRUST_BADGES = [];

const RootGrid = styled(Grid2)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  margin: 0,
  padding: 0,
  minHeight: '640px',
  height: 'auto',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  backgroundColor: brandTokens.navy,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(81.6deg, #012F47 20.64%, rgba(1, 47, 71, 0.35) 74.41%)',
    zIndex: 1,
  },

  [theme.breakpoints.up('xl')]: {
    minHeight: '800px',
  },

  [theme.breakpoints.down('sm')]: {
    minHeight: '100vh',
    alignItems: 'flex-start',

    '&::before': {
      height: '55%',
      background: 'linear-gradient(180deg, #012F47 20.64%, rgba(1, 47, 71, 0) 100%)',
      bottom: 'auto',
      zIndex: 2,
    },
  },
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '100%',
  minHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'cover',
  zIndex: 0,

  '@media (min-aspect-ratio: 16/9)': {
    width: '100%',
    height: 'auto',
  },

  '@media (max-aspect-ratio: 16/9)': {
    width: 'auto',
    height: '100%',
  },
});

const VideoContainer = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
});

const LeftGrid = styled(Grid2)(({ theme }) => ({
  minHeight: '640px',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(6),
  position: 'relative',
  zIndex: 2,
  maxWidth: '900px',

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
    maxWidth: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(4),
    padding: theme.spacing(4, 3),
    minHeight: 'auto',
  },
}));

const ContentBlock = styled('div')(({ theme }) => ({
  color: '#FFF',
  maxWidth: '100%',
  width: '100%',
  padding: theme.spacing(4, 3),
  position: 'relative',
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    textAlign: 'left',
    padding: theme.spacing(6, 1),
    paddingTop: '8%',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  fontSize: '14px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: brandTokens.cyan,
  fontFamily: 'GT Haptik, Roboto, sans-serif',
}));

const TitleMain = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  marginBottom: theme.spacing(2),
  fontFamily: 'GT Haptik, Raleway, sans-serif',
  fontWeight: 700,
  fontSize: '48px',
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  maxWidth: '640px',
  position: 'relative',
  zIndex: 2,

  [theme.breakpoints.down('lg')]: {
    fontSize: '40px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '34px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    lineHeight: 1.2,
    maxWidth: '100%',
  },
}));

const GradientWord = styled('span')({
  background: brandTokens.signatureGradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

const SubTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  marginBottom: theme.spacing(4),
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: 1.6,
  maxWidth: '520px',
  color: '#8FA3B8',
  position: 'relative',
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    maxWidth: '100%',
  },
}));

const RightGrid = styled(Grid2)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const GlassPreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  padding: theme.spacing(3),
  maxWidth: 320,
  background: 'rgba(30, 32, 34, 0.45)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderRadius: 16,
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.35)',
}));

const PreviewHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const SamplePdfButton = styled(Button)({
  marginTop: 16,
  padding: '4px 8px',
  minWidth: 0,
  alignSelf: 'flex-start',
  fontFamily: 'Roboto, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: 0,
  color: brandTokens.onSurfaceVariant,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: brandTokens.cyan,
  },
  '& .MuiButton-startIcon': {
    marginRight: 6,
    '& > *:nth-of-type(1)': {
      fontSize: 16,
    },
  },
});

const TrustRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
}));

const TrustChip = styled(Box)({
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid rgba(107, 211, 253, 0.2)',
  background: 'rgba(26, 28, 30, 0.6)',
  fontFamily: 'Roboto, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  color: brandTokens.onSurfaceVariant,
});

const PlateSearch = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showTrust = HERO_TRUST_BADGES.length > 0;
  const [samplePdfOpen, setSamplePdfOpen] = useState(false);

  return (
    <RootGrid container>
      <VideoContainer>
        <VideoBackground autoPlay muted loop playsInline>
          <source
            src={isMobile ? background_auto_valid_mobile : background_auto_valid}
            type="video/mp4"
          />
        </VideoBackground>
      </VideoContainer>

      <LeftGrid size={{ xs: 12, sm: 12, md: 7, lg: 6 }}>
        <ContentBlock>
          <Eyebrow>Consulta veicular</Eyebrow>
          <TitleMain variant="h1">
            Descubra <GradientWord>tudo</GradientWord> sobre o carro antes de fechar negócio
          </TitleMain>
          <SubTitle>
            Débitos, leilão, sinistro, KM, restrições e mais informações essenciais
            entregues em segundos — com a transparência Autovalid.
          </SubTitle>
          <SearchLicensePlate />
          {showTrust && (
            <TrustRow>
              {HERO_TRUST_BADGES.map((badge) => (
                <TrustChip key={badge.id}>{badge.label}</TrustChip>
              ))}
            </TrustRow>
          )}
        </ContentBlock>
      </LeftGrid>

      <RightGrid size={{ md: 5, lg: 6 }}>
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(107, 211, 253, 0.1)',
              filter: 'blur(100px)',
              borderRadius: '50%',
            }}
          />
          <GlassPreview>
            <PreviewHeader>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
                aria-hidden="true"
              >
                <Box
                  component="img"
                  src={reportPreviewSymbol}
                  alt=""
                  sx={{ width: 40, height: 40, objectFit: 'contain' }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'GT Haptik, Roboto, sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    color: brandTokens.onSurface,
                  }}
                >
                  Preview do relatório
                </Typography>
                <Typography sx={{ fontSize: 12, color: brandTokens.onSurfaceVariant }}>
                  Exemplo ilustrativo
                </Typography>
              </Box>
            </PreviewHeader>
            <SamplePdfButton
              variant="text"
              size="small"
              startIcon={<PictureAsPdfOutlinedIcon />}
              onClick={() => setSamplePdfOpen(true)}
            >
              Ver PDF exemplo
            </SamplePdfButton>
          </GlassPreview>
        </Box>
      </RightGrid>

      <SampleReportPdfModal
        open={samplePdfOpen}
        onClose={() => setSamplePdfOpen(false)}
      />
    </RootGrid>
  );
};

export default PlateSearch;
